import { SideOfStreetAdherenceLevel } from 'data-access';

export enum DisplayRestrictionTypeEnum {
  AlwaysOff = 0,
  AlwaysOn = 1,
  BasedOnTruckRestrictions = 2,
}

export enum FerriesEnum {
  Use = 0,
  Avoid = 1,
}

export enum HazmatTypeEnum {
  None = 0,
  General = 1,
  Corrosive = 2,
  Explosives = 3,
  Flammable = 4,
  Inhalants = 5,
  Radioactive = 6,
  HarmfulToWater = 7,
}

export enum RoutingTypeEnum {
  Practical = 0,
  Shortest = 1,
  Fastest = 2,
}

export enum TollRoadTypeEnum {
  AlwaysAvoid = 1,
  AvoidIfPossible = 2,
  Use = 3,
}

export enum TunnelCategoryEnum {
  NONE = 0,
  TunnelBCDE = 1,
  TunnelCDE = 2,
  TunnelDE = 3,
  TunnelE = 4,
}

export enum UnitsOfMeasurementEnum {
  Imperial = 0,
  Metric = 1,
}

export enum VehicleTypeEnum {
  Auto = 'Auto', // 0
  Truck = 'Heavy Duty', // 3
  Bus = 'Bus', // 4
  LightDuty = 'Light Duty', // 11
  MidsizeTruck = 'Medium Duty', // 12
}

export interface CoPilotOptionsModel {
  displayRestrictions: DisplayRestrictionTypeEnum;
}

export interface RestrictionZonesModel {
  allow: Array<string>;
  avoid: Array<string>;
  warn: Array<string>;
}

export interface VehicleConfigurationModel {
  height: number;
  width: number;
  length: number;
  weight: number;
  maxWeightPerAxleGroup?: number;
}

export interface VehicleDimensionsModel {
  units: UnitsOfMeasurementEnum;
  height: number;
  length: number;
  width: number;
  weight: number;
  maxWeightPerAxleGroup?: number;
}

export interface VehicleOptionsModel {
  vehicleConfig: VehicleConfigurationModel;
  governorSpeedLimit?: number;
  hazmatType?: HazmatTypeEnum;
  tunnelCategory?: TunnelCategoryEnum;
}

export interface VehicleDimensionsThesholdsModel {
  vehicleType: VehicleTypeEnum;
  vehicleDimensionMin: VehicleDimensionsModel;
  vehicleDimensionMax: VehicleDimensionsModel;
}

export interface RoutingOptionsModel {
  ferries?: FerriesEnum;
  restrictionZones?: RestrictionZonesModel;
  useStateAndNationalNetwork?: boolean;
  routeType: RoutingTypeEnum;
  tollRoads: TollRoadTypeEnum;
  useOpenBorders?: boolean;
  elevationLimit?: number;
  sideOfStreetAdherenceLevel?: SideOfStreetAdherenceLevel;
  useAvoidsAndFavors?: boolean;
  useCustomRoadSpeeds?: boolean;
  useTraffic?: boolean;
}

export interface RoutingProfileModel {
  routingProfileId: number;
  name: string;
  isAccountDefault?: boolean;
  vehicleType: VehicleTypeEnum;
  units: UnitsOfMeasurementEnum;
  vehicleOptions?: VehicleOptionsModel;
  routingOptions?: RoutingOptionsModel;
  copilotOptions?: CoPilotOptionsModel;
}

export interface DefaultRoutingProfilesModel {
  profiles: RoutingProfileModel[];
  vehicleDimensionMinMax: VehicleDimensionsThesholdsModel[];
}

export interface PagedCollection<T> {
  href: string;
  method: string;
  offset: number;
  limit: number;
  size: number;
  first: {
    href: string;
    method: string;
  };
  previous: {
    href: string;
    method: string;
  };
  next: {
    href: string;
    method: string;
  };
  last: {
    href: string;
    method: string;
  };
  data: T;
  dateCollected: string;
}

// TrimbleMaps.RoutingProfile.Service.Models.Responses.ApiError class
export enum ErrorCode {
  UNKNOWN_ERROR = -1,
  INVALID_DISPLAY_RESTRICTION_TYPE = -1000,
  INVALID_VEHICLE_HEIGHT = -1001,
  INVALID_VEHICLE_WIDTH = -1002,
  INVALID_VEHICLE_LENGTH = -1003,
  INVALID_VEHICLE_WEIGHT = -1004,
  INVALID_HAZMAT_TYPE = -1005,
  INVALID_TUNNEL_TYPE = -1006,
  INVALID_ZONE = -1007,
  INVALID_ROUTING_TYPE = -1008,
  INVALID_TOLLROAD_TYPE = -1009,
  INVALID_REGION = -1010,
  INVALID_VEHICLE_TYPE = -1011,
  INVALID_FERRIES_TYPE = -1012,
  ROUTING_PROFILE_NAME_LENGTH_EXCEEDED = -1013,
  ROUTING_PROFILE_NAME_CONSECUTIVE_WHITESPACES = -1014,
  ROUTING_PROFILE_NAME_MISPLACED_WHITESPACES = -1015,
  ROUTING_PROFILE_NAME_SPECIAL_CHARACTERS = -1016,
  INVALID_ELEVATION_LIMIT = -1017,
  INVALID_REGIONAL_ATTRIBUTE = -1018,
  INVALID_ATTRIBUTE_COMBINATION = -1019,
  INVALID_ZONE_ASSIGNMENT = -1020,
  INVALID_GOVERNOR_SPEED_LIMIT = -1021,
  INVALID_VEHICLE_WEIGHT_PER_AXLE = -1022,
  ROUTING_PROFILE_CREATE_FAILED = -1100,
  ROUTING_PROFILE_UPDATE_FAILED = -1101,
  ACCOUNT_NOT_FOUND = -1102,
  ACCOUNT_OWNER_NOT_FOUND = -1103,
  ROUTING_PROFILE_NOT_FOUND = -1104,
  DUPLICATE_ROUTING_PROFILE_NAME = -1105,
  DEFAULT_ROUTING_PROFILE_NAME = -1106,
  MISSING_REQUIRED_PARAMETER = -1107,
  IDENTITY_NOT_FOUND = -1108,
  IDENTITY_TYPE_NOT_FOUND = -1109,
  IDENTITY_TYPE_NOT_SUPPORTED = -1110,
  REGION_NOT_FOUND = -1111,
}

// TrimbleMaps.RoutingProfile.Service.Models.Responses.ApiError class
export interface ProfileApiError {
  message: string;
  detail: string;
  errorcode: ErrorCode;
}

// TrimbleMaps.RoutingProfile.Service.Models.Responses.ResponseModel class
export interface ProfileResponseModel<T> {
  message: string;
  data: T;
  error?: ProfileApiError;
  warnings?: Array<string>;
}
